import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  Upload as UploadIcon, 
  FileText, 
  X, 
  CheckCircle,
  Loader2,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Upload() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedDeck, setUploadedDeck] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: "include"
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = useCallback((selectedFile) => {
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.ms-powerpoint'];
    const fileName = selectedFile.name.toLowerCase();
    
    if (!validTypes.includes(selectedFile.type) && !fileName.endsWith('.pdf') && !fileName.endsWith('.pptx') && !fileName.endsWith('.ppt')) {
      toast.error("Please upload a PDF or PowerPoint file");
      return;
    }

    if (selectedFile.size > 50 * 1024 * 1024) {
      toast.error("File size must be less than 50MB");
      return;
    }

    setFile(selectedFile);
    setUploadedDeck(null);
  }, []);

  const removeFile = () => {
    setFile(null);
    setUploadedDeck(null);
  };

  const uploadFile = async () => {
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => Math.min(prev + 10, 90));
    }, 200);

    try {
      const response = await fetch(`${BACKEND_URL}/api/decks/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      setUploadedDeck(data);
      toast.success("Pitch deck uploaded successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const analyzeDeck = async () => {
    if (!uploadedDeck) return;

    setAnalyzing(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/decks/${uploadedDeck.deck_id}/analyze`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Analysis failed');
      }

      toast.success("Analysis complete!");
      navigate(`/analysis/${uploadedDeck.deck_id}`);
    } catch (error) {
      toast.error(error.message || "Failed to analyze deck");
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Upload Your Pitch Deck
          </h1>
          <p className="text-slate-600">
            Upload a PDF or PowerPoint file and let AI analyze your pitch
          </p>
        </div>

        {!uploadedDeck ? (
          <>
            {/* Upload Zone */}
            <Card 
              className={`border-2 border-dashed transition-all ${
                dragActive 
                  ? 'border-teal-500 bg-teal-50' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <CardContent className="p-12">
                {!file ? (
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-2xl bg-teal-500/10 flex items-center justify-center mx-auto mb-4">
                      <UploadIcon className="w-8 h-8 text-teal-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      Drop your file here
                    </h3>
                    <p className="text-slate-500 mb-6">
                      or click to browse
                    </p>
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      accept=".pdf,.pptx,.ppt"
                      onChange={handleFileInput}
                      data-testid="file-input"
                    />
                    <label htmlFor="file-upload">
                      <Button asChild className="btn-primary cursor-pointer">
                        <span data-testid="browse-files-btn">Browse Files</span>
                      </Button>
                    </label>
                    <p className="text-sm text-slate-400 mt-4">
                      PDF or PowerPoint • Max 50MB
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{file.name}</p>
                          <p className="text-sm text-slate-500">
                            {(file.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={removeFile}
                        disabled={uploading}
                        data-testid="remove-file-btn"
                      >
                        <X className="w-5 h-5 text-slate-500" />
                      </Button>
                    </div>

                    {uploading && (
                      <div className="space-y-2">
                        <Progress value={uploadProgress} className="h-2" />
                        <p className="text-sm text-slate-500 text-center">
                          Uploading... {uploadProgress}%
                        </p>
                      </div>
                    )}

                    <Button 
                      onClick={uploadFile}
                      disabled={uploading}
                      className="w-full btn-secondary"
                      data-testid="upload-btn"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <UploadIcon className="w-4 h-4 mr-2" />
                          Upload Pitch Deck
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <Card className="border border-slate-200">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-teal-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-teal-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                Upload Complete!
              </h3>
              <p className="text-slate-600 mb-6">
                Your pitch deck "{uploadedDeck.filename}" has been uploaded successfully.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={analyzeDeck}
                  disabled={analyzing}
                  className="btn-secondary"
                  data-testid="analyze-btn"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      Analyze & Match Investors
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setFile(null);
                    setUploadedDeck(null);
                  }}
                  className="rounded-full"
                  data-testid="upload-another-btn"
                >
                  Upload Another
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tips */}
        <div className="mt-8 p-6 bg-slate-100 rounded-xl">
          <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-teal-600" />
            Tips for Best Results
          </h4>
          <ul className="space-y-2 text-sm text-slate-600">
            <li>• Include company stage and funding ask in your deck</li>
            <li>• Clearly state your therapeutic focus area</li>
            <li>• Highlight key team credentials and experience</li>
            <li>• Mention any existing traction or milestones</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
